import { useState, useEffect } from 'react';
import { Stack, Title, Text, Group, Button, Paper, List, ThemeIcon, Alert, Collapse } from '@mantine/core';
import { IconShoppingCart, IconArrowRight, IconArrowLeft, IconCheck, IconAlertCircle, IconInfoCircle } from '@tabler/icons-react';
import { useAuth } from '../../hooks/useAuth';
import { usePostHog } from '@posthog/react';
import { AnalyticsEvent } from '../../utils/analytics';
import { initiateKrogerConnection } from '../../utils/krogerAuth';
import { KrogerLocationPicker } from './KrogerLocationPicker';
import { ProductPreferencesStep } from './ProductPreferencesStep';
import { ProductPreferences, KrogerInfo } from '../../types/familyProfile';
import { KrogerLocation } from '../../types/kroger';
import { HouseholdSegment } from '../../utils/householdSegment';

type GroceryConnectionStepProps = {
  value: {
    krogerConnected: boolean;
    krogerInfo?: KrogerInfo;
    productPreferences?: ProductPreferences;
  };
  onChange: (value: {
    krogerConnected: boolean;
    krogerInfo?: KrogerInfo;
    productPreferences?: ProductPreferences;
  }) => void;
  onNext: () => void;
  onBack: () => void;
  isLastStep: boolean;
  isCompleting?: boolean;
  householdSegment?: HouseholdSegment;
};

export const GroceryConnectionStep = ({ value, onChange, onNext, onBack, isLastStep, isCompleting = false, householdSegment }: GroceryConnectionStepProps) => {
  const { user } = useAuth();
  const posthog = usePostHog();
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showProductPreferences, setShowProductPreferences] = useState(false);

  // Check for OAuth callback parameters and load existing location
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const krogerStatus = urlParams.get('kroger');
    const errorParam = urlParams.get('error');

    if (krogerStatus === 'connected' && !value.krogerConnected) {
      // Only update if we're not already connected (prevents stale URL params from affecting state)
      posthog?.capture(AnalyticsEvent.KrogerConnected, { source: 'onboarding' });
      onChange({
        krogerConnected: true,
        krogerInfo: value.krogerInfo,
        productPreferences: value.productPreferences
      });
    }

    // Always clean up URL parameters if they exist
    if (krogerStatus || errorParam) {
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (errorParam) {
      const errorMessages: Record<string, string> = {
        kroger_auth_failed: 'Kroger authorization was denied or failed.',
        missing_params: 'Missing required parameters from Kroger.',
        invalid_state: 'Invalid security token.',
        token_exchange_failed: 'Failed to exchange authorization code.',
      };
      setError(errorMessages[errorParam] || 'An unknown error occurred.');
    }
  }, [onChange, value.krogerConnected, value.krogerInfo, value.productPreferences, posthog]);


  const handleConnectKroger = async () => {
    if (!user) {
      setError('You must be signed in to connect Kroger.');
      return;
    }

    setIsConnecting(true);
    setError(null);

    try {
      await initiateKrogerConnection(user);
    } catch (error) {
      console.error('Error initiating Kroger OAuth:', error);
      setError('Failed to connect to Kroger. Please try again.');
      setIsConnecting(false);
    }
  };

  const handleLocationSelect = (location: KrogerLocation, fulfillmentPreference: 'pickup' | 'delivery') => {
    if (!user) {
      setError('You must be signed in to select a location.');
      return;
    }

    setError(null);
    onChange({ 
      ...value,
      krogerInfo: {
        ...(value.krogerInfo || {}),
        location,
        fulfillmentPreference,
      },
    });
  };

  const handleProductPreferencesChange = (productPreferences: ProductPreferences) => {
    onChange({
      ...value,
      productPreferences
    });
  };

  const handleBackFromProductPreferences = () => {
    setShowProductPreferences(false);
    // Scroll to top when going back to grocery connection
    window.scrollTo(0, 0);
  };

  const handleNext = () => {
    // If Kroger is connected but we haven't shown product preferences yet, show them
    if (value.krogerConnected && value.krogerInfo?.location && !showProductPreferences) {
      setShowProductPreferences(true);
      // Scroll to top when showing product preferences
      window.scrollTo(0, 0);
    } else {
      // Otherwise proceed to next step
      onNext();
    }
  };

  // Determine if we should show "Continue" vs "Complete Setup"
  const shouldShowContinue = value.krogerConnected && value.krogerInfo?.location && !showProductPreferences;


  // If showing product preferences, render that step instead
  if (showProductPreferences) {
    return (
      <ProductPreferencesStep
        value={value.productPreferences || {}}
        onChange={handleProductPreferencesChange}
        onNext={onNext}
        onBack={handleBackFromProductPreferences}
        isLastStep={isLastStep}
        isCompleting={isCompleting}
        householdSegment={householdSegment}
      />
    );
  }

  return (
    <Stack gap="xl" w={{ base: '100%', sm: 700 }} mx="auto" px={{ base: 'sm', sm: 'md' }}>
      {/* Header */}
        <Stack gap="md" ta="center" align="center">
          <ThemeIcon size={80} radius="xl" variant="gradient" gradient={{ from: 'orange', to: 'red' }}>
            <IconShoppingCart size={40} />
          </ThemeIcon>
          <Stack gap="xs" align="center">
            <Title order={2} size="h1" fw={700} c="dark">
              Grocery store connection
            </Title>
            <Text size="lg" c="dimmed" ta="center" maw={400}>
              Connect to Kroger for seamless shopping, or use Instacart for any store
            </Text>
          </Stack>
        </Stack>

      {/* Error Alert */}
      <Collapse in={!!error}>
        <Alert icon={<IconAlertCircle size={16} />} color="red" radius="lg">
          {error}
        </Alert>
      </Collapse>

      {/* Grocery Connection */}
        <Paper 
          radius="lg" 
          p={{ base: 'md', sm: 'xl' }}
          style={{
            border: '1px solid var(--mantine-color-gray-2)',
            width: '100%'
          }}
        >
          <Stack gap="xl">
            {/* Kroger Connection */}
            <Stack gap="md" align="center">
              <Stack gap="xs" align="center">
                <Text fw={600} size="lg">Kroger</Text>
                <Text size="sm" c="dimmed">Available in 35+ states</Text>
              </Stack>

              {value.krogerConnected ? (
                <Stack gap="md" style={{ width: '100%' }}>
                  <Paper 
                    p="md" 
                    radius="lg" 
                    bg="green.0" 
                    shadow="none"
                    style={{ 
                      border: '1px solid var(--mantine-color-green-3)',
                      width: '100%'
                    }}
                  >
                    <Group gap="sm" align="center">
                      <ThemeIcon 
                        size={32} 
                        radius="md" 
                        color="green" 
                        variant="light"
                      >
                        <IconCheck size={18} />
                      </ThemeIcon>
                      
                      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                        <Text fw={600} size="md" c="green.7" style={{ wordBreak: 'break-word' }}>
                          Connected successfully!
                        </Text>
                        <Text size="xs" c="green.6" style={{ wordBreak: 'break-word' }}>
                          {value.krogerInfo?.location ? 'Store selected' : 'Please select your store'}
                        </Text>
                      </Stack>
                    </Group>
                  </Paper>

                  {/* Location Selection - show when Kroger is connected */}
                  {value.krogerConnected && (
                    <KrogerLocationPicker
                      onLocationSelect={handleLocationSelect}
                      selectedLocationId={value.krogerInfo?.location?.locationId}
                      selectedFulfillmentPreference={value.krogerInfo?.fulfillmentPreference}
                    />
                  )}

                </Stack>
              ) : (
                <Button 
                  size="md"
                  leftSection={<IconShoppingCart size={18} />}
                  onClick={handleConnectKroger}
                  loading={isConnecting}
                  disabled={isConnecting}
                  color="orange"
                >
                  {isConnecting ? 'Connecting...' : 'Connect Kroger account'}
                </Button>
              )}
            </Stack>

            {/* Benefits - only show if not connected */}
            {!value.krogerConnected && (
              <>
                <Stack gap="md" align="center">
                  <Text fw={600} size="lg">Benefits of connecting</Text>
                  
                  <List
                    spacing="sm"
                    size="sm"
                    icon={
                      <ThemeIcon color="green" size={20} radius="xl">
                        <IconCheck size={12} />
                      </ThemeIcon>
                    }
                  >
                    <List.Item>Integrated shopping list</List.Item>
                    <List.Item>AI personal grocery shopper</List.Item>
                    <List.Item>1-click meal plan add-to-cart</List.Item>
                  </List>
                </Stack>

                {/* Without Connection */}
                <Alert
                  color="blue"
                  variant="light"
                  icon={<IconInfoCircle size={16} />}
                  title="Don't shop at Kroger?"
                  radius="lg"
                >
                  <Text size="sm">
                    No problem! We also integrate with Instacart for shopping at any store. 
                    You'll get complete meal plans with detailed grocery lists either way.
                  </Text>
                </Alert>
              </>
            )}
          </Stack>
        </Paper>

      {/* Status Message */}
      {value.krogerConnected && value.krogerInfo?.location && (
        <Stack gap="md">
          <Paper 
            p="lg" 
            radius="lg" 
            bg="gray.0" 
            style={{ 
              border: '1px solid var(--mantine-color-gray-2)',
              minHeight: 80,
              width: '100%'
            }}
          >
            <Group gap="md" align="center" style={{ height: '100%' }}>
              <ThemeIcon 
                size={40} 
                radius="md" 
                color="green" 
                variant="light"
              >
                <IconCheck size={20} />
              </ThemeIcon>
              
              <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                <Text fw={600} c="green.7" style={{ wordBreak: 'break-word' }}>
                  All set for grocery shopping!
                </Text>
                <Text size="sm" c="dimmed" style={{ wordBreak: 'break-word' }}>
                  Kroger connected with store location selected
                </Text>
              </Stack>
            </Group>
          </Paper>

        </Stack>
      )}

      {/* Navigation */}
        <Group justify="space-between" pt="md" wrap="nowrap" className="wizard-footer">
          <Button 
            variant="subtle" 
            size="md"
            leftSection={<IconArrowLeft size={18} />}
            onClick={onBack}
            color="orange"
          >
            Back
          </Button>
          
          <Button
            size="md"
            rightSection={<IconArrowRight size={18} />}
            onClick={handleNext}
            color="orange"
            variant="filled"
            loading={isCompleting && !shouldShowContinue}
          >
            {shouldShowContinue ? 'Continue' : (isLastStep ? 'Finish' : 'Continue')}
          </Button>
        </Group>
    </Stack>
  );
};
